import React, { useEffect } from "react";
import { SafeAreaView, View, StyleSheet, ScrollView, Image } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useNavigation } from "@react-navigation/native";
import { Grid, Container, Row, Column } from "ui-grid";
import { RootState } from "../../redux/reducers";
import { typography, Text, theme, OnScreenAlert, Button } from "ui-components";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { translate } from "../../i18n/translate";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { BottomNav } from "../../components/layout/bottom-nav";
import { Footer } from "../../components/new/footer/footer";
import { cleanState } from "../../redux/actions/actionCreators";
import { CheckType } from "../../enums/check";
import { toHumanDollars } from "../../utils/money";

const PaymentFlowPaymentMethodMobileCheckPaymentInstructions = ({ route }: HomeScreenProps) => {
  const { params } = route;
  const { propName: propertyName, checkTypeProp } = params;
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const logger = useSelector((state: RootState) => state.logger);
  const navigateToCheckScanPayment = () => {
    dispatch(cleanState());
    return navigation.navigate("PaymentFlowPaymentMethodMobileCheckPaymentProcessing", {
      ...params,
    });
  };
  useEffect(() => {
    let title =
      checkTypeProp == CheckType.PERSONAL
        ? (translate("paymentFlow.paymentMethodMobilePersonalCheck.workflowTitle") as string)
        : (translate("paymentFlow.paymentMethodMobileMoneyOrder.workflowTitle") as string);
    navigation.setParams({ headerTitle: title });
  }, []);
  const Heading = ({ heading }: any) => {
    return (
      <Row>
        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
        <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow, styles.header]}>
          <View style={styles.inputRowInner}>
            <Text
              style={[
                typography.heading2,
                { fontFamily: "Lato_700Bold", color: theme.colors.text3 },
              ]}
              {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayInstructions
                .instructionHeading}>
              {heading}
            </Text>
          </View>
        </Column>
      </Row>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}>
        <View>
          <Grid>
            <Container>
              <View style={{ margin: 4 }} />
              <Heading
                heading={translate(
                  "paymentFlow.checkScanInstructions.mobileCheckPaymentInstructions"
                )}
              />
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={styles.inputRowInner}>
                    <View style={{ flex: 1 }}>
                      <View style={{ margin: 4 }} />
                      <InstructionCard
                        title={
                          translate(
                            "paymentFlow.checkScanInstructions.mobileCheckPaymentInstructionsHeader1"
                          ) as string
                        }
                        body={
                          translate(
                            "paymentFlow.checkScanInstructions.mobileCheckPaymentInstructionsText1"
                          ) as string
                        }
                        source={require("../../../assets/check-pay-instruction-1.png")}
                        {...automationTestingLocators
                          .paymentFlowPaymentMethodMobileCheckPayInstructions.instructionCard1}
                      />
                      <View style={{ margin: 8 }} />
                      <InstructionCard
                        title={
                          translate(
                            "paymentFlow.checkScanInstructions.mobileCheckPaymentInstructionsHeader2"
                          ) as string
                        }
                        body={`${translate(
                          "paymentFlow.checkScanInstructions.mobileCheckPaymentInstructionsText2"
                        )} ${propertyName}`}
                        source={require("../../../assets/check-pay-instruction-2.png")}
                        {...automationTestingLocators
                          .paymentFlowPaymentMethodMobileCheckPayInstructions.instructionCard2}
                      />
                      <View style={{ margin: 8 }} />

                      <InstructionCard
                        title={
                          translate(
                            "paymentFlow.checkScanInstructions.mobileCheckPaymentInstructionsHeader3"
                          ) as string
                        }
                        body={
                          translate(
                            "paymentFlow.checkScanInstructions.mobileCheckPaymentInstructionsText3"
                          ) as string
                        }
                        source={require("../../../assets/check-pay-instruction-3.png")}
                        {...automationTestingLocators
                          .paymentFlowPaymentMethodMobileCheckPayInstructions.instructionCard3}
                      />
                      <View style={{ margin: 25 }} />

                      <OnScreenAlert
                        alertType="caution"
                        body={
                          translate(
                            "paymentFlow.checkScanInstructions.mobileCheckPaymentInstructionsWarning"
                          ) as string
                        }
                        bodyStyle={typography.title2}
                        {...automationTestingLocators
                          .paymentFlowPaymentMethodMobileCheckPayInstructions
                          .checkPaymentInstructionsWarning}
                      />
                      <View style={{ margin: 2 }} />
                    </View>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
          </Grid>
        </View>
      </ScrollView>
      <BottomNav backgroundColor={theme.colors.white}>
        <View style={{ margin: 2 }} />
        <Row>
          <Column span={{ xs: false, sm: 3, md: 3, lg: 3 }} />
          <Column span={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <Button
              text={translate("paymentFlow.checkScanInstructions.next") as string}
              mode="contained"
              onPress={() => {
                navigateToCheckScanPayment();
              }}
              {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayInstructions
                .nextButton}
            />
          </Column>
          <Column span={{ xs: false, sm: 3, md: 3, lg: 3 }} />
        </Row>
      </BottomNav>
      <Footer />
    </SafeAreaView>
  );
};

interface InstructionCardProps {
  title?: string;
  body?: string;
  source?: any;
  testID: string;
  accessibilityLabel: string;
}
const InstructionCard = (props: InstructionCardProps) => {
  let { source, body, title } = props;
  return (
    <OnScreenAlert
      testID={props.testID}
      accessibilityLabel={props.accessibilityLabel}
      title={title}
      body={body}
      renderImage={
        <View style={styles.instructionImageContainer}>
          <Image source={source} style={{ height: 45, width: 45 }} />
        </View>
      }
      borderColor={theme.colors.stroke}
      titleColor={theme.colors.text3}
      bodyStyle={{
        ...typography.subHeading,
        color: theme.colors.text2,
        fontFamily: "Lato_400Regular",
      }}
      titleStye={{ fontFamily: "Lato_700Bold" }}
    />
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: theme.colors.white,
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        backgroundColor: theme.colors.white,
      },
      iosTablet: {
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        backgroundColor: theme.colors.white,
      },
      androidTablet: {
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollView: { flexGrow: 0 },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: theme.colors.white, paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      iosPhone: { backgroundColor: theme.colors.white, paddingBottom: 200 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      androidPhone: { backgroundColor: theme.colors.white, paddingBottom: 200 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
    }),
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    flex: 1,
    flexDirection: "row",
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
    }),
  },
  header: {
    ...domusoResponsiveStyleHelper({
      webPhone: { paddingVertical: 3 },
      webTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      webDesktop: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      iosPhone: { paddingVertical: 3 },
      iosTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      androidPhone: { paddingVertical: 3 },
      androidTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
    }),
  },
  instructionImageContainer: {
    borderWidth: 1,
    padding: 8,
    borderRadius: 10,
    borderColor: theme.colors.stroke,
    height: 64,
    width: 64,
  },
});
export default PaymentFlowPaymentMethodMobileCheckPaymentInstructions;
